import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Pagination,
  Select,
  Text,
} from "@mantine/core";
import { IconArrowsSort, IconGripVertical } from "@tabler/icons-react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  getBranchCategoryOverrides,
  resetBranchCategoryRanks,
  setBranchCategoryRanks,
} from "@/api/branches";
import { getCategories } from "@/api/category";
import { getProducts } from "@/api/products";
import { useAccess } from "@/hooks/useAccess";
import { byBranchRank } from "@/lib/branchOrder";
import { PER_PAGE_OPTIONS } from "@/lib/format";
import { notify } from "@/lib/notify";
import { lockProps } from "@/lib/permissions";
import {
  ListCell,
  ListHeader,
  ListRow,
  ListScroll,
} from "@/components/DataList";
import { EMPTY_LIST } from "@/lib/queryClient";

// Satır menüdeki karşılığını taşır: görsel, ad, ürün sayısı ve bu şubede gizliyse uyarısı.
// Sıralarken "hangi kategori neyi kapsıyor" bilgisi olmadan sürüklemek körlemesine olurdu.
function SortableCategoryRow({ category, productCount, hidden, disabled }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: category.id,
    disabled,
  });

  return (
    <ListRow
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.55 : 1,
        background: isDragging ? "var(--mantine-color-chocolate-0)" : undefined,
      }}
    >
      <ListCell w={36}>
        <ActionIcon
          variant="subtle"
          color="gray"
          {...attributes}
          {...listeners}
          disabled={disabled}
          style={{
            cursor: disabled ? "not-allowed" : "grab",
            touchAction: "none",
          }}
        >
          <IconGripVertical size={18} />
        </ActionIcon>
      </ListCell>
      <ListCell flex ta="left">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <Avatar src={category.imageUrl} radius="md" size={32}>
            {category.name?.[0]?.toUpperCase()}
          </Avatar>
          <Text size="sm" fw={500} truncate c={hidden ? "dimmed" : undefined}>
            {category.name}
          </Text>
          {hidden && (
            <Badge size="xs" variant="light" color="gray">
              bu şubede gizli
            </Badge>
          )}
        </Group>
      </ListCell>
      <ListCell w={90}>
        <Text size="xs" c="dimmed">
          {productCount} ürün
        </Text>
      </ListCell>
    </ListRow>
  );
}

// Şubeye özel kategori sırası. Marka sırası (Kategoriler sayfasındaki sürükleme) tenant geneli
// kalır; buradaki sıra yalnızca bu şubenin QR menüsünü etkiler. Ürün sırası ayrı bir liste değil —
// Fiyatlar sekmesindeki tablodan sürükleniyor, ürünler zaten orada.
export function BranchCategoryOrder({ branch }) {
  const queryClient = useQueryClient();
  const { can } = useAccess();
  const canOrder = can("products") || can("categories");

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const { data: categories = EMPTY_LIST, isLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });
  const { data: products = EMPTY_LIST } = useQuery({
    queryKey: ["products"],
    queryFn: getProducts,
  });
  const { data: overrides = EMPTY_LIST } = useQuery({
    queryKey: ["branch-category-overrides", branch.id],
    queryFn: () => getBranchCategoryOverrides(branch.id),
  });

  const overrideByCategoryId = useMemo(
    () => new Map(overrides.map((override) => [override.categoryId, override])),
    [overrides],
  );
  const productCountByCategoryId = useMemo(() => {
    const counts = new Map();

    for (const product of products) {
      counts.set(product.categoryId, (counts.get(product.categoryId) ?? 0) + 1);
    }

    return counts;
  }, [products]);

  const ordered = useMemo(
    () =>
      byBranchRank(
        categories,
        (item) => overrideByCategoryId.get(item.id)?.rank,
      ),
    [categories, overrideByCategoryId],
  );

  // Sunucudaki sıra kaynak; sürükleme sırasında yerel state gösteriliyor ki istek dönene kadar
  // liste zıplamasın.
  useEffect(() => setItems(ordered), [ordered]);

  const customized = overrides.some((override) => override.rank !== null);
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: ["branch-category-overrides", branch.id],
    });

  const rankMutation = useMutation({
    mutationFn: (next) =>
      setBranchCategoryRanks(
        branch.id,
        next.map((item, index) => ({ categoryId: item.id, rank: index })),
      ),
    onError: () => notify.error("Kategori sırası kaydedilemedi"),
    // Hata durumunda da tazele: sunucudaki sıra UI'daki yalanı düzeltir.
    onSettled: invalidate,
  });

  const resetMutation = useMutation({
    mutationFn: () => resetBranchCategoryRanks(branch.id),
    onSuccess: () => notify.success("Kategori sırası markaya döndürüldü"),
    onError: () => notify.error("Sıra sıfırlanamadı"),
    onSettled: invalidate,
  });

  const totalPages = Math.max(1, Math.ceil(items.length / perPage));
  const pageItems = items.slice((page - 1) * perPage, page * perPage);

  // Sürükleme sayfa içinde olsa da sıra TAM liste üzerinden yazılır — 2. sayfada yapılan bir
  // değişiklik 1. sayfanın sırasını bozmaz.
  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }

    const next = arrayMove(
      items,
      items.findIndex((item) => item.id === active.id),
      items.findIndex((item) => item.id === over.id),
    );

    setItems(next);
    rankMutation.mutate(next);
  };

  if (isLoading) {
    return (
      <Group justify="center" p="xl">
        <Loader />
      </Group>
    );
  }

  return (
    <>
      <Card withBorder radius="md" p={0}>
        <ListScroll minWidth={486} fit={false}>
          <ListHeader
            columns={[
              { label: "", w: 36 },
              { label: "Kategori", flex: true, ta: "left" },
              { label: "İçerik", w: 90 },
            ]}
          />
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={pageItems.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {pageItems.map((item) => (
                <SortableCategoryRow
                  key={item.id}
                  category={item}
                  productCount={productCountByCategoryId.get(item.id) ?? 0}
                  hidden={overrideByCategoryId.get(item.id)?.isActive === false}
                  disabled={!canOrder}
                />
              ))}
            </SortableContext>
          </DndContext>
        </ListScroll>
      </Card>

      {/* Üç kolon: sayfa ayarları solda, sıfırlama gerçekten ortada, pagination sağda.
                space-between olsaydı buton sola yapışırdı. */}
      <Box
        mt="sm"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          gap: "var(--mantine-spacing-sm)",
        }}
      >
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" c="dimmed">
            Sayfa başı
          </Text>
          <Select
            withCheckIcon={false}
            w={75}
            size="xs"
            allowDeselect={false}
            data={PER_PAGE_OPTIONS}
            value={String(perPage)}
            onChange={(value) => {
              setPerPage(Number(value));
              setPage(1);
            }}
          />
          <Text size="sm" c="dimmed">
            {items.length} kategori
          </Text>
        </Group>
        <Button
          size="compact-sm"
          variant="subtle"
          leftSection={<IconArrowsSort size={14} />}
          loading={resetMutation.isPending}
          {...lockProps(canOrder, { disabled: !customized })}
          onClick={() => resetMutation.mutate()}
        >
          Marka sırasına dön
        </Button>
        <Group justify="flex-end" wrap="nowrap">
          <Pagination
            withEdges
            total={totalPages}
            value={page}
            onChange={setPage}
            size="sm"
          />
        </Group>
      </Box>
    </>
  );
}
